import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const Profile: React.FC = () => {
  const { user, loading, error, updateDisplayName } = useAuth();
  const [newDisplayName, setNewDisplayName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  // Initialize display name when user data is loaded
  useEffect(() => {
    if (user && user.displayName) {
      setNewDisplayName(user.displayName);
    }
  }, [user]);
  
  // Handle display name update
  const handleUpdateDisplayName = async () => {
    if (newDisplayName.trim() === '') return;
    
    try {
      await updateDisplayName(newDisplayName);
      setIsEditing(false);
      setUpdateSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to update display name:', err);
    }
  };
  
  // If not authenticated, redirect to home
  if (!loading && !user) {
    return <Navigate to="/" />;
  }
  
  if (loading || !user) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="animate-pulse flex flex-col space-y-4">
          <div className="h-12 bg-gray-800 rounded-xl w-1/3"></div>
          <div className="h-64 bg-gray-800 rounded-xl w-full"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-outfit font-bold text-white mb-6">Your Profile</h1>
      
      <div className="bg-[#121216] border border-gray-800 rounded-xl overflow-hidden shadow-lg">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-[#FF4D8C]/20 to-transparent p-6 border-b border-gray-800">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 rounded-xl overflow-hidden flex items-center justify-center text-white text-3xl font-medium">
              <div className="w-full h-full bg-[#FF4D8C] flex items-center justify-center">
                {user.displayName?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-outfit font-bold text-white">{user.displayName}</h2>
              <p className="text-gray-400">@{user.username}</p>
            </div>
          </div>
        </div>
        
        {/* Profile Content */}
        <div className="p-6">
          <h3 className="text-xl font-medium text-white mb-4">Profile Details</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Display Name
              </label>
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3"
                    autoFocus
                  />
                  <button
                    onClick={handleUpdateDisplayName}
                    className="px-4 py-3 bg-naia-pink hover:bg-opacity-80 text-white rounded-xl transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setNewDisplayName(user.displayName || '');
                    }}
                    className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={user.displayName}
                    disabled
                    className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3"
                  />
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                  >
                    Edit
                  </button>
                </div>
              )}
              {updateSuccess && (
                <p className="mt-1 text-xs text-green-400">
                  Display name updated successfully!
                </p>
              )}
              {error && (
                <p className="mt-1 text-xs text-red-400">
                  {error}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                This is how your name will appear to other users
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Username
              </label>
              <input
                type="text"
                value={user.username}
                disabled
                className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3"
              />
              <p className="mt-1 text-xs text-gray-500">
                Your unique identifier on Naia
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Email
              </label>
              <div className="flex items-center">
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3"
                />
                {(user as any).emailVerified ? (
                  <span className="ml-2 px-2 py-1 bg-green-900/30 text-green-400 text-xs rounded-lg">
                    Verified
                  </span>
                ) : (
                  <span className="ml-2 px-2 py-1 bg-yellow-900/30 text-yellow-400 text-xs rounded-lg">
                    Unverified
                  </span>
                )}
              </div>
              {!(user as any).emailVerified && (
                <p className="mt-1 text-xs text-gray-400">
                  Please check your inbox to verify your email
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
